import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { getUserInfo, updateUserInfo, USER_INFO_QUERY_KEY } from '@/api/profile-settings';
import { getUserStoreState, useUserStore } from '@/store';
import type { UserSettings } from '@/store/user/slices/settings/initial-state';

interface UserInfo {
  id?: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  color?: string | number;
  emailNotification?: boolean;
  subscription?: boolean;
  status?: number;
  isNewUser?: boolean;
  first_login?: boolean;
  is_first_time?: boolean;
  creation_date?: string;
  update_date?: string;
  last_tx_time?: string;
  doc_type?: string;
}

export const useUserInfo = (isAuthenticated: boolean) => {
  return useQuery<UserInfo>({
    queryKey: USER_INFO_QUERY_KEY,
    queryFn: () => getUserInfo(),
    staleTime: 1000 * 60 * 5,
    retry: false,
    enabled: isAuthenticated,
  });
};

export const useCurrentUser = () => {
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);
  return useUserInfo(isAuthenticated);
};

export const useUpdateUserInfo = () => {
  const queryClient = useQueryClient();
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);

  return useMutation({
    mutationFn: async (body: {
      first_name: string;
      last_name: string;
      email: string;
      color?: string | number;
      emailNotification?: boolean;
      subscription?: boolean;
    }) => {
      const payload = {
        first_name: body.first_name,
        last_name: body.last_name,
        email: body.email,
        ...(body.color !== undefined && { color: body.color }),
        ...(body.emailNotification !== undefined && { emailNotification: body.emailNotification }),
        ...(body.subscription !== undefined && { subscription: body.subscription }),
      };

      return updateUserInfo(payload);
    },
    onError: () => {
      notification.error({
        message: "We couldn't update the info",
        className: 'custom-error-notification bold-notification-message',
      });
    },
    onSuccess: (_data, variables) => {
      const store = getUserStoreState();
      const currentUser = store.currentUser;

      // The PATCH endpoint only returns a success message, not the updated
      // profile. Update the Zustand store directly with the payload so the
      // UI reflects the change immediately.
      if (currentUser) {
        store.updateUserProfile({
          ...currentUser,
          user: {
            ...currentUser.user,
            firstName: variables.first_name,
            lastName: variables.last_name,
            email: variables.email,
          },
        });
      }

      const settingsPatch: Partial<UserSettings> = {};
      const normalizedColor = variables.color !== undefined ? String(variables.color) : undefined;

      if (normalizedColor !== undefined) {
        settingsPatch.color = normalizedColor;
      }
      if (variables.emailNotification !== undefined) {
        settingsPatch.emailNotification = variables.emailNotification;
      }
      if (variables.subscription !== undefined) {
        settingsPatch.subscription = variables.subscription;
      }

      if (Object.keys(settingsPatch).length > 0) {
        store.updateSettings(settingsPatch);
      }

      queryClient.setQueryData(USER_INFO_QUERY_KEY, (oldData: UserInfo | undefined) => ({
        ...oldData,
        first_name: variables.first_name,
        last_name: variables.last_name,
        email: variables.email,
        ...(normalizedColor !== undefined && { color: normalizedColor }),
        ...(variables.emailNotification !== undefined && { emailNotification: variables.emailNotification }),
        ...(variables.subscription !== undefined && { subscription: variables.subscription }),
      }));

      queryClient.invalidateQueries({ queryKey: USER_INFO_QUERY_KEY });

      notification.success({
        message: 'Info updated successfully',
        pauseOnHover: true,
        className: 'custom-success-notification bold-notification-message',
      });
    },
  });
};

export const useCurrentUserAvatar = () => {
  const { data: userInfo, isLoading, error } = useCurrentUser();

  return {
    username: userInfo?.username || userInfo?.first_name,
    color: userInfo?.color,
    isLoading,
    error,
  };
};

export const useChangePassword = () => {
  const queryClient = useQueryClient();
  const { token } = useUserStore.getState();

  return useMutation({
    mutationFn: async (body: { oldPassword: string; newPassword: string }) => {
      if (!token) {
        throw new Error('User not authenticated');
      }

      const response = await fetch('/api/user/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Failed to change password');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USER_INFO_QUERY_KEY });
      notification.success({
        message: 'Password changed successfully',
        pauseOnHover: true,
        className: 'custom-success-notification bold-notification-message',
      });
    },
    onError: () => {
      notification.error({
        message: 'Failed to change password',
        pauseOnHover: true,
        className: 'custom-error-notification bold-notification-message',
      });
    },
  });
};
